import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import OptimizedImage from '../components/OptimizedImage';
import { Calendar, Tag as TagIcon, Folder, ArrowLeft, Share2, Facebook, Twitter, Linkedin, Link as LinkIcon, Check, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const SITE = 'https://1staveicp.com';

const formatDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return ''; }
};

const ShareButtons = ({ url, title }) => {
  const [copied, setCopied] = useState(false);
  const encoded = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* no-op */ }
  };

  const btn = 'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-colors';

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="share-buttons">
      <span className="text-sm font-bold text-gray-600 flex items-center gap-1"><Share2 className="w-4 h-4" /> Share:</span>
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${encoded}`}
        target="_blank"
        rel="noopener noreferrer"
        className={`${btn} bg-[#1877F2] text-white hover:bg-[#166FE5]`}
        data-testid="share-facebook"
      >
        <Facebook className="w-4 h-4" /> Facebook
      </a>
      <a
        href={`https://twitter.com/intent/tweet?url=${encoded}&text=${encodedTitle}`}
        target="_blank"
        rel="noopener noreferrer"
        className={`${btn} bg-black text-white hover:bg-gray-800`}
        data-testid="share-twitter"
      >
        <Twitter className="w-4 h-4" /> X
      </a>
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`}
        target="_blank"
        rel="noopener noreferrer"
        className={`${btn} bg-[#0A66C2] text-white hover:bg-[#094FA3]`}
        data-testid="share-linkedin"
      >
        <Linkedin className="w-4 h-4" /> LinkedIn
      </a>
      <button
        type="button"
        onClick={copy}
        className={`${btn} bg-gray-200 text-gray-800 hover:bg-gray-300`}
        data-testid="share-copy-link"
      >
        {copied ? <><Check className="w-4 h-4" /> Copied!</> : <><LinkIcon className="w-4 h-4" /> Copy link</>}
      </button>
    </div>
  );
};

const NewsletterBox = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setStatus(null);
    try {
      const res = await fetch(`${API}/api/blog/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'blog-post' }),
      });
      if (res.ok) {
        setStatus({ ok: true, msg: "Thanks! You're in the Scoop Club." });
        setEmail('');
      } else {
        setStatus({ ok: false, msg: 'Something went wrong. Try again?' });
      }
    } catch {
      setStatus({ ok: false, msg: 'Network error. Try again?' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#E91E8C] to-[#9B4DCA] text-white rounded-3xl p-6 shadow-xl">
      <div className="flex items-center gap-2 mb-2">
        <Mail className="w-5 h-5" />
        <h3 className="text-xl font-black" style={{ fontFamily: 'Rowdies, cursive' }}>Scoop Club</h3>
      </div>
      <p className="text-white/90 text-sm mb-4">More posts like this, straight to your inbox.</p>
      <form onSubmit={submit} className="space-y-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          className="w-full px-4 py-3 rounded-full text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-white/30"
          data-testid="post-newsletter-email-input"
        />
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-white text-[#E91E8C] font-bold py-3 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50"
          data-testid="post-newsletter-submit-btn"
        >
          {loading ? 'Subscribing…' : 'Subscribe'}
        </button>
      </form>
      {status && <p className="text-sm mt-3 text-white">{status.msg}</p>}
    </div>
  );
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const embedRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await fetch(`${API}/api/blog/posts/${encodeURIComponent(slug)}`);
        if (res.status === 404) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        if (!res.ok) throw new Error('fetch failed');
        const data = await res.json();
        setPost(data);

        const rel = await fetch(`${API}/api/blog/posts/${encodeURIComponent(slug)}/related?limit=3`);
        if (rel.ok) {
          const rd = await rel.json();
          setRelated(rd.related || []);
        }
      } catch (e) {
        console.error('Post load failed:', e);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
    window.scrollTo(0, 0);
  }, [slug]);

  // Load TikTok / Instagram embed scripts if embed_html was provided
  useEffect(() => {
    if (!post?.embed_html) return;
    const html = post.embed_html;
    if (html.includes('tiktok')) {
      const s = document.createElement('script');
      s.src = 'https://www.tiktok.com/embed.js';
      s.async = true;
      document.body.appendChild(s);
      return () => { try { document.body.removeChild(s); } catch { /* */ } };
    }
    if (html.includes('instagram')) {
      const s = document.createElement('script');
      s.src = 'https://www.instagram.com/embed.js';
      s.async = true;
      document.body.appendChild(s);
      return () => { try { document.body.removeChild(s); } catch { /* */ } };
    }
  }, [post?.embed_html]);

  if (loading) {
    return <div className="min-h-screen pt-32 flex items-center justify-center text-2xl font-black">Loading…</div>;
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen pt-32 flex flex-col items-center justify-center gap-4 text-center px-6">
        <h1 className="text-4xl font-black" style={{ fontFamily: 'Rowdies, cursive' }}>Post not found</h1>
        <p className="text-gray-600">That post may have moved or is no longer published.</p>
        <Link to="/blog" className="inline-flex items-center gap-2 bg-[#E91E8C] text-white px-6 py-3 rounded-full font-bold">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>
      </div>
    );
  }

  const url = `${SITE}/blog/${post.slug}`;

  // JSON-LD Article schema for LLM + search visibility
  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.seo_description || post.excerpt || '',
    image: post.hero_image_url ? [post.hero_image_url] : undefined,
    datePublished: post.publish_date,
    dateModified: post.updated_at || post.publish_date,
    author: { '@type': 'Organization', name: post.author_display || '1st Ave ICP' },
    publisher: {
      '@type': 'Organization',
      name: '1st Ave ICP',
      logo: { '@type': 'ImageObject', url: `${SITE}/images/misc/favicon-logo.png` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    keywords: [...(post.tags || []), ...(post.voice_search_terms || [])].join(', ') || undefined,
  };

  return (
    <div
      className="min-h-screen pt-32 bg-white"
      style={{ backgroundImage: 'url(/images/misc/storefront-bg.jpg)', backgroundSize: 'cover', backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}
    >
      <SEO
        title={post.seo_title || post.title}
        description={post.seo_description || post.excerpt || ''}
        keywords={[...(post.tags || []), ...(post.categories || []), ...(post.voice_search_terms || [])].join(', ')}
        canonical={`/blog/${post.slug}`}
        type="article"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />

      <article className="bg-white">
        {/* Hero */}
        <header className="relative bg-gradient-to-br from-[#E91E8C] to-[#9B4DCA] pt-16 pb-24 px-6 text-white">
          <div className="max-w-4xl mx-auto">
            <Link to="/blog" className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-6 text-sm font-bold">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>
            {post.categories?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {post.categories.map((c) => (
                  <Link key={c} to={`/blog/category/${encodeURIComponent(c)}`} className="inline-block bg-white/20 backdrop-blur hover:bg-white/30 text-white text-xs font-black px-3 py-1 rounded-full">
                    {c}
                  </Link>
                ))}
              </div>
            )}
            <h1 className="text-4xl md:text-6xl font-black mb-4 leading-tight" style={{ fontFamily: 'Rowdies, cursive' }} data-testid="blog-post-title">
              {post.title}
            </h1>
            {post.subtitle && <p className="text-xl md:text-2xl text-white/90 mb-6 max-w-3xl">{post.subtitle}</p>}
            <div className="flex flex-wrap items-center gap-4 text-sm text-white/80">
              <span className="font-bold">By {post.author_display || '1st Ave ICP'}</span>
              <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {formatDate(post.publish_date)}</span>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0">
            <svg className="w-full h-16" viewBox="0 0 1440 64" preserveAspectRatio="none">
              <path fill="#ffffff" d="M0,32 C360,64 720,0 1080,32 C1260,48 1380,48 1440,32 L1440,64 L0,64 Z" />
            </svg>
          </div>
        </header>

        {/* Body */}
        <div className="max-w-4xl mx-auto px-6 py-12">
          {post.hero_image_url && (
            <div className="rounded-3xl overflow-hidden mb-10 shadow-xl">
              <OptimizedImage src={post.hero_image_url} alt={post.hero_image_alt || post.title} className="w-full h-auto object-cover" />
            </div>
          )}

          {/* Rendered markdown content */}
          <div
            className="blog-content prose prose-lg max-w-none prose-headings:font-black prose-headings:text-gray-900 prose-a:text-[#E91E8C] prose-a:no-underline hover:prose-a:underline prose-img:rounded-2xl prose-img:shadow-lg"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="blog-post-content"
            dangerouslySetInnerHTML={{ __html: post.content_html || '' }}
          />

          {/* Embed (TikTok / Instagram) */}
          {post.embed_html && (
            <div
              ref={embedRef}
              className="my-10 flex justify-center"
              data-testid="blog-post-embed"
              dangerouslySetInnerHTML={{ __html: post.embed_html }}
            />
          )}

          {/* Tags */}
          {post.tags?.length > 0 && (
            <div className="mt-10 pt-6 border-t">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-bold text-gray-600 flex items-center gap-1"><TagIcon className="w-4 h-4" /> Tags:</span>
                {post.tags.map((t) => (
                  <Link key={t} to={`/blog/tag/${encodeURIComponent(t)}`} className="text-xs bg-gray-100 hover:bg-pink-100 hover:text-[#E91E8C] text-gray-700 px-3 py-1 rounded-full transition-colors">
                    {t}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Share */}
          <div className="mt-8 pt-6 border-t">
            <ShareButtons url={url} title={post.title} />
          </div>

          {/* Newsletter */}
          <div className="mt-12">
            <NewsletterBox />
          </div>

          {/* Related */}
          {related.length > 0 && (
            <div className="mt-16">
              <h2 className="text-3xl font-black mb-6 flex items-center gap-2" style={{ fontFamily: 'Rowdies, cursive' }}>
                <Folder className="w-7 h-7 text-[#E91E8C]" /> Related Reads
              </h2>
              <div className="grid sm:grid-cols-3 gap-6">
                {related.map((r) => (
                  <Link
                    key={r.id || r.slug}
                    to={`/blog/${r.slug}`}
                    className="group block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all border"
                    data-testid={`related-post-${r.slug}`}
                  >
                    {r.hero_image_url ? (
                      <div className="aspect-[16/10] overflow-hidden bg-gray-100">
                        <img src={r.hero_image_url} alt={r.hero_image_alt || r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      </div>
                    ) : (
                      <div className="aspect-[16/10] bg-gradient-to-br from-[#E91E8C] to-[#9B4DCA] flex items-center justify-center text-4xl">🍦</div>
                    )}
                    <div className="p-4">
                      <h3 className="font-black text-gray-900 group-hover:text-[#E91E8C] mb-1" style={{ fontFamily: 'Rowdies, cursive' }}>{r.title}</h3>
                      {r.excerpt && <p className="text-xs text-gray-600 line-clamp-2">{r.excerpt}</p>}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </div>
  );
};

export default BlogPost;
